import { makeAutoObservable } from 'mobx';
import { IAuth } from './authentication/IAuth';
import { SPAuth } from './authentication/SPAuth';
import PersonalSettingsState from './PersonalSettingsState';
import API, {
	EntityType,
	EntityTypes,
	ISettings,
	IUserConfigPackage,
} from '../API';
import DeviceState from './Device';
import * as microsoftTeams from '@microsoft/teams-js';
import { MobileAttachmentsHarnessState } from '../outlook/MobileAttachmentsHarness';
import { OutlookCtrlState } from '../outlook/OutlookCtrl';
import { OutlookMessageMode } from './CorporateArchiveStates/LegalEntityDocumentsStateOutlook';
import { DocumentViewerDialogState } from '../components/controls/documents/DocumentViewerDialog';
import { CombinedSearchCtrlState } from '../components/controls/CombinedSearchCtrl';
import EntityDashboardState from './EntityDashboardState';

import { StandaloneSearchTabState } from '../components/pages/StandaloneSearchTab';
import { MSALConfigGen } from '../authConfig';
import { CurrentUserAccountState } from '../components/controls/CurrentUserAccount';
import { Pages } from '../Pages';
import { FullAppHomeState } from '../modern/FullAppHome';
import { EntityDetailsState } from '../components/controls/EntityDetails';

export interface IRootStateProps {
	RootState: RootState;
}


export class RootState {

	public detailsState?: EntityDetailsState;

	public currentUserAccountState?: CurrentUserAccountState;
	toggleIsNavCollapsed(): void {
		this.isNavCollapsed = !this.isNavCollapsed;
	}
	
	//Section for routing/URL handling
	public hash: string = ''; //The hash contains the arguments
	public page: string = 'entities'; //The page is the first part of the hash
	public argument: string | undefined; //Second part of the hash e.g. project ID
	public argument2: string | undefined; //Third part of the hash e.g. task ID

	//Stuff to do with login
	public name: string = '';

	public spAuth: SPAuth;

	public settingsState?: PersonalSettingsState;
	public userId: string = '';

	//True if we're just viewing a dashboard or some other page where we don't want chrome.
	public hidePageNavigation: boolean = false;
	public isNavCollapsed:boolean = true;
	public hidePageHeader: boolean = false;

	public showCreateTaskDialog: boolean = false;

	public canNavigateBack: boolean =
		document.referrer.replace(/\/$/, '') === MSALConfigGen.getMsalConfig().auth.redirectUri;

	public auth: IAuth;

	public isUserInfoLoaded: boolean = false;
	public device: DeviceState;

	private previouslyLoadedPages: string[] = [];

	public entityTypesState?: EntityDashboardState;

	public searchCtrlState?: CombinedSearchCtrlState;

	public mailHarness?: MobileAttachmentsHarnessState;
	public mailOutlook?: OutlookCtrlState;
	
	public searchTab?:StandaloneSearchTabState;

	public fullAppHome?: FullAppHomeState;

	public userConfig?: IUserConfigPackage;

	public messageMode: OutlookMessageMode;

	//Used when we're just showing a page with only one document viewer
	public standaloneDocViewerState?: DocumentViewerDialogState;

	constructor(auth: IAuth, uninitialized?: boolean) {
		makeAutoObservable(this);

		this.auth = auth;
		this.messageMode = OutlookMessageMode.None;
		this.device = new DeviceState();
		this.spAuth = new SPAuth(this.auth.getToken, () => {});

		if (!uninitialized) {
			//Bit of a hack, don't start the outlook state events if this isn't the real RootState instance.
			if ((window.location.hash || '').indexOf('mailCompose') >= 0)
				this.messageMode = OutlookMessageMode.Compose;
			if ((window.location.hash || '').indexOf('mailRead') >= 0)
				this.messageMode = OutlookMessageMode.Read;
		}
	}

	public async initialize(): Promise<void> {
		if (this._initialLoaded) return;

		this._initialLoaded = true;

		if (this.isUserInfoLoading || this.isUserInfoLoaded) {
			return;
		}

		this.isUserInfoLoading = true;

		try {
			await this.loadSettings();
			this.setIsUserInfoLoaded(true);
		} finally {
			this.isUserInfoLoading = false;
		}

		this.setRoute(
			window.location.hash || Pages.fullAppHome,
		);
	}

	public useCachedSettings:boolean = false;

	async loadSettings() {
		
		if(this.useCachedSettings) {
			//Load the settings from local storage
			let cachedSettings = localStorage.getItem('userSettings');
			if(cachedSettings) {
				try {
					let parsedSettings:ISettings = JSON.parse(cachedSettings);
					this.setSettings(parsedSettings);
						
					setTimeout(() => {
						//Don't do it immediately to avoid slowing start times
						this.reloadAndStoreSettings();
					}, 5000);

					return;
				} catch {
					//Failed to parse - they'll be reloaded for the next refresh
				}
				
			}
		} 

		let token = await this.auth.getToken();
		let userSettings = await new API(token).getSettings();
		this.setSettings(userSettings);
		localStorage.setItem('userSettings', JSON.stringify(userSettings));
	}
	
	async reloadAndStoreSettings() {

		//Load settings anyway and store them in case there was an update
		let token = await this.auth.getToken();
		var api = new API(token);
		api.getSettings().then((freshSettings) => {
			localStorage.setItem('userSettings', JSON.stringify(freshSettings));
		});

		api.userPing();
	}

	setSettings(userSettings: ISettings) {
		this.userConfig = {
			getToken: this.auth.getToken.bind(this.auth),
			entityTypes: new EntityTypes(userSettings.entityTypes),
			currentUserId: userSettings.currentUserId,
			tenantUrl: userSettings.tenantUrl,
			isAdmin: userSettings.isAdmin,
			tenantId: userSettings.tenantId,
			outlookPreventAttachments: userSettings.outlookPreventAttachments,
		};

		this.currentUserAccountState = new CurrentUserAccountState(this.userConfig);

		this.settingsState = new PersonalSettingsState(this.userConfig, userSettings);
		this.settingsState.setUserId(this.userId); //userId may or may not be available yet

		/*
		this.mailHarness = new MobileAttachmentsHarnessState(
			this.userConfig,
			this.messageMode,
		);*/
		this.detailsState = new EntityDetailsState(this.userConfig);
		this.mailOutlook = new OutlookCtrlState(this.userConfig, this.messageMode);
		this.fullAppHome = new FullAppHomeState(this.userConfig, this.detailsState);
		this.standaloneDocViewerState = new DocumentViewerDialogState(
			this.userConfig,
		);
		this.entityTypesState = new EntityDashboardState(
			EntityType.EntityTypeDefinition,
			this.userConfig,
			this.detailsState
		);

		this.searchTab = new StandaloneSearchTabState(this.userConfig);
	}

	public setRoute(hash: string) {
		this.hash = hash.replace('#', '');
		let hashParts = this.hash.split('/');

		this.page = hashParts[0];
		this.argument = decodeURIComponent(hashParts[1] || '');
		this.argument2 = decodeURIComponent(hashParts[2] || '');

		if (this.page.startsWith(Pages.documentViewer)) {
			this.standaloneDocViewerState?.show(this.argument, '');
			this.standaloneDocViewerState?.docViewer?.documentPreviewState?.setHeaderMargin(
				100,
			);
		}

		if (this.page.endsWith('Tab') || this.page.startsWith(Pages.mail)) {
			this.hidePageNavigation = true;
		}

		//The remainder here is only to retrieve data for initial load of the page. After that point, it doesn't have to be loaded again.
		if (this.previouslyLoadedPages.indexOf(this.page) >= 0) return;

		//Store it so we know we've loaded the data.
		this.previouslyLoadedPages = this.previouslyLoadedPages.concat(this.page);
	}

	public toggleCreateTaskDialog(templateId?: string | undefined): void {
		this.showCreateTaskDialog = !this.showCreateTaskDialog;
	}

	setCurrentUserInfo(user: microsoftTeams.app.UserInfo | undefined) {
		this.userId = user?.id || '';
		this.settingsState?.setUserId(this.userId);
	}

	static isOAuthRedirect = (): boolean => {
		let hash = (window.location.hash || '').replace('#', '');
		return hash.startsWith('code=') || hash.startsWith('id_token=');
	};

	static getUrlParameter = (sParam: string) => {
		var sPageURL = window.location.search.substring(1),
			sURLVariables = sPageURL.split('&'),
			sParameterName,
			i;

		for (i = 0; i < sURLVariables.length; i++) {
			sParameterName = sURLVariables[i].split('=');

			if (sParameterName[0] === sParam) {
				return sParameterName[1] === undefined
					? ''
					: decodeURIComponent(sParameterName[1]);
			}
		}
		return '';
	};

	private _initialLoaded = false;

	public isUserInfoLoading: boolean = false;

	public setIsUserInfoLoading(isLoading: boolean) {
		this.isUserInfoLoading = isLoading;
	}

	public setIsUserInfoLoaded(isLoaded: boolean) {
		this.isUserInfoLoaded = isLoaded;
	}
}
